/**
 * "Preencha para mover" (#568): a etapa de destino exige um campo vazio.
 *
 * O servidor recusa o movimento com a lista do que falta. Em vez de mostrar a
 * recusa e deixar a pessoa caçar o campo na ficha, a tela pede só aqueles
 * campos, no painel lateral, e ao salvar grava e move de uma vez. Sem
 * preencher, o card fica onde estava.
 */

import { useState, type ReactNode } from 'react';
import type { CardDoFunil, MudancaDeCampos } from '@cliqcrm/contracts';
import { CamposEditaveis, obrigatoriosVazios } from '@/entities/campos/campo-personalizado';
import { useCamposPersonalizados } from '@/shared/api/consultas';
import { Botao, Dialogo } from '@/shared/ui';

export interface MovimentoPendente {
  readonly card: CardDoFunil;
  readonly etapa: string;
  readonly nomeDaEtapa: string;
  /** Os campos que o servidor disse faltar. */
  readonly campos: readonly string[];
  readonly perda?: unknown;
}

export function PreencherParaMover({
  pendente,
  salvando,
  erro,
  aoSalvar,
  aoFechar,
}: {
  pendente: MovimentoPendente;
  salvando: boolean;
  erro: string | null;
  aoSalvar: (mudanca: MudancaDeCampos) => void;
  aoFechar: () => void;
}): ReactNode {
  const catalogo = useCamposPersonalizados('oportunidade');
  const [mudanca, definirMudanca] = useState<MudancaDeCampos>({});
  const pedidos = (catalogo.data?.itens ?? []).filter((campo) =>
    pendente.campos.includes(campo.id),
  );
  // Aqui todos os pedidos são obrigatórios: a etapa de destino é que os exige.
  const faltando = obrigatoriosVazios(
    pedidos.map((campo) => ({ ...campo, obrigatorio: true, obrigatorio_desde_etapa: null })),
    pendente.card.campos,
    mudanca,
  );

  return (
    <Dialogo
      titulo={`Preencha para mover para ${pendente.nomeDaEtapa}`}
      forma="lateral"
      aoFechar={() => {
        if (!salvando) aoFechar();
      }}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar} desabilitado={salvando} motivo="Salvando…">
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="preencher-para-mover"
            variante="primario"
            desabilitado={faltando.length > 0 || salvando}
            motivo={
              salvando ? 'Salvando…' : `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
            }
          >
            {salvando ? 'Salvando…' : 'Salvar e mover'}
          </Botao>
        </>
      }
    >
      <form
        id="preencher-para-mover"
        className="funil__preencher"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (faltando.length === 0) aoSalvar(mudanca);
        }}
      >
        <p className="dialogo__texto">
          A etapa {pendente.nomeDaEtapa} pede estes campos de &ldquo;
          {pendente.card.titulo ?? 'oportunidade sem nome'}&rdquo;. Sem eles, o card fica onde está.
        </p>
        <CamposEditaveis
          id="preencher-para-mover-campo"
          campos={pedidos}
          valores={pendente.card.campos}
          mudanca={mudanca}
          aoMudar={definirMudanca}
        />
        {erro && (
          <p className="dialogo__erro" role="alert">
            {erro}
          </p>
        )}
      </form>
    </Dialogo>
  );
}
